import dayjs from "dayjs";

import { isMobile, isTablet } from "react-device-detect";
import { findNode } from "@/shared/utils/content/content";
import { getIsIos } from "@/shared/utils/device";
import { arraymove, sortByArray } from "@/shared/utils/array";
import { CONTENT_CLASSES } from "@/shared/utils/content/content.constants";
import { getTouches } from "@/shared/utils/dom";

export const COOKIE_KEY_APP_OPEN_NUMBER = "openNumber";

// Delay between open last read book of ML
export const DelayOpenML = 1000 * 60 * 60;

export const SearchQueryLimit = 500;

export const MOUSE_BUTTON_CODE = {
  main: 0, // Main button, usually the left button.
  middle: 1, //  Auxiliary button, usually the wheel button or the middle button (if present).
  secondary: 2, // Secondary button, usually the right button.
};

/**
 * @readonly
 * @enum {number}
 */
export const scOptions = {
  SELECTION: "selection",
  CHAPTER: "CHAPTER",
  RANGE: "RANGE",
};

/**
 * @readonly
 * @enum {string}
 */
export const HistoryType = {
  READING: "read",
  LISTEN: "audio",

  LIBRARY: "download_history",

  ML_HISTORY: "library",

  SEARCH: "search_history",
};

export const getNodeIds = (node, array, ids = []) => {
  if (node?.length) {
    for (let i = 0; i < node.length; i++) {
      ids.push(node[i].id);
      getNodeIds(node[i].children, array, ids);
    }
  }

  return ids;
};

export const getNodePathIds = (node, id) => {
  const path = [];
  findNode(id, node, "id", path);
  return path.map((child) => child.id);
};

export const insertParasToBook = (book, paras) => {
  if (!paras.length || !book?.children?.length) {
    return;
  }

  const bookChildren = book.children;
  const firstChildPuborder = bookChildren[0].puborder;

  let para;
  for (let i = 0; i < paras.length; i++) {
    para = paras[i];

    // Lower {puborder} than {firstChildPuborder} means there is no holder for {para}.
    // (e.g. paragraph of the book title, title chapters are cut off from the "children",
    // because the title chapter would be the same as the book title in the "Tree").
    if (para.puborder < firstChildPuborder) {
      continue;
    }

    insertParaToNodeByPuborder(book, para);
  }
};

const insertParaToNodeByPuborder = (node, para) => {
  if (node.className === CONTENT_CLASSES.PARAGRAPH) {
    // eslint-disable-next-line no-console
    console.log(
      "%c Paragraph is not allowed as the node! Use book or chapter!",
      "background: black; color: white;",
    );
  }

  const paraPuborder = para.puborder;
  const paraParentChildrenSpread = [...node.children];
  const paraParentChildrenSpreadLength = paraParentChildrenSpread.length;

  if (paraParentChildrenSpreadLength) {
    if (paraParentChildrenSpread[0].className === CONTENT_CLASSES.PARAGRAPH) {
      insertParaToChapterByPuborder(node, para);
    } else {
      let childCurr, childNext, childCurrPuborder, childNextPuborder;
      for (let i = 0; i < paraParentChildrenSpreadLength; i++) {
        childCurr = paraParentChildrenSpread[i];
        childNext = paraParentChildrenSpread[i + 1];
        childCurrPuborder = childCurr.puborder;
        childNextPuborder = childNext?.puborder;

        // Skip push if {para} is already in the book as either a paragraph or a chapter.
        // The priority has a chapter as it has the same {content} as a para.
        if (paraPuborder === childCurrPuborder || paraPuborder === childNextPuborder) {
          return;
        }

        if (
          childCurrPuborder < paraPuborder &&
          (!childNextPuborder || childNextPuborder > paraPuborder)
        ) {
          insertParaToNodeByPuborder(childCurr, para);
        }
      }
    }
  } else {
    node.children = [para];
  }
};

const insertParaToChapterByPuborder = (chapter, para) => {
  const paraPuborder = para.puborder;
  const childrenSpread = [...chapter.children];
  const childrenSpreadLength = childrenSpread.length;

  if (childrenSpreadLength) {
    let childNextPuborder;

    for (let i = 0; i < childrenSpreadLength; i++) {
      const puborderDelta = paraPuborder - childrenSpread[i].puborder;
      childNextPuborder = childrenSpread[i + 1]?.puborder;

      // Skip push if {para} is already in the book as either a paragraph or a chapter.
      // The priority has a chapter as it has the same {content} as a para.
      if (puborderDelta === 0 || (childNextPuborder && childNextPuborder === paraPuborder)) {
        return;
      }

      if (puborderDelta === -1) {
        // push before the item
        chapter.children = [];
        if (i > 0) {
          chapter.children.push(...childrenSpread.slice(0, i));
        }
        chapter.children.push(para);
        chapter.children.push(...childrenSpread.slice(i));
        break;
      }

      if (puborderDelta === 1) {
        // push after the item
        chapter.children = [];
        chapter.children.push(...childrenSpread.slice(0, i + 1));
        chapter.children.push(para);
        if (i < childrenSpreadLength) {
          chapter.children.push(...childrenSpread.slice(i + 1));
        }
        break;
      }
    }
  } else {
    chapter.children = [para];
  }
};

export const trimBr = (html) => html.replace(/^( |<br\s*[/]?>)*(.*?)( |<br\s*[/]?>)*$/, "$2");

export const generateParasHtml = (readerItems, { refs, headers, pageBreaks } = {}) => {
  let container = document.createElement("div");
  let firstPara, lastPara;
  let lastPageNumber;

  readerItems.forEach((item, index) => {
    const type = item.element_type;
    const isParagraphItem = type === "p";

    const wrap = document.createElement("div");
    wrap.className = CONTENT_CLASSES.PARAGRAPH;
    wrap.dataset.id = item.id;
    const element = document.createElement(type);

    let formedContent = item.content;
    if (isParagraphItem && refs) {
      formedContent += `<span class="${CONTENT_CLASSES.REF_CODE}">${item.refcode_short}</span>`;
    }

    element.innerHTML = `${formedContent} `;

    if (index === 0) {
      firstPara = wrap;
    }
    if (index === readerItems.length - 1) {
      lastPara = wrap;
    }

    if (headers && isParagraphItem) {
      container.insertAdjacentHTML(
        "beforeend",
        `<div class="${CONTENT_CLASSES.READER_HEADER_TEXT}">${item.refcode_long}</div>`,
      );
    }

    wrap.appendChild(element);
    container.appendChild(wrap);

    if (pageBreaks && isParagraphItem) {
      if (lastPageNumber !== item.refcode_2) {
        lastPageNumber = item.refcode_2;
        container.insertAdjacentHTML(
          "beforeend",
          `<div class="${CONTENT_CLASSES.PAGE_NUMBER}">${item.refcode_2}</div>`,
        );
      }
    }
  });

  return { container, firstPara, lastPara };
};

export const getIdFromElement = (element) => {
  const { parentElement, id } = element;
  if (id) {
    return id;
  }
  return parentElement ? getIdFromElement(parentElement) : null;
};

export const fixDuplicateRefCodes = (options) => {
  let noDupOptions = options.filter((item) => item.refcode_short !== "");
  let duplicates = {};

  noDupOptions.forEach((item) => {
    const refcode = item.refcode_short;
    if (!duplicates[refcode]) {
      duplicates[refcode] = [];
    }
    duplicates[refcode].push(item);
  });

  Object.keys(duplicates).forEach((item) => {
    const indicies = duplicates[item];
    if (indicies.length >= 2) {
      indicies.forEach((option, index) => {
        option.refCodePostFix = index;
      });
    }
  });
  return noDupOptions;
};

export const collectAllNodes = (node, holder, classes = []) => {
  if (node.children) {
    node.children.forEach((item) => {
      if (classes.indexOf(item.className) === -1 || classes.length === 0) {
        holder.push(item);
      }
      collectAllNodes(item, holder, classes);
    });
  }
};

/**
 * @description Returns x coordinate of the received event
 * or the last x coordinate of the touches of the received event.
 * @param {MouseEvent|TouchEvent} e
 * @returns {number|undefined}
 */
export const getCoordinateX = (e) => {
  let x = e.x ?? e.clientX;

  if (x === undefined) {
    const touches = getTouches(e);
    if (touches?.length) {
      const lastTouch = touches[touches.length - 1];
      if (lastTouch) {
        x = lastTouch.clientX;
      }
    }
  }

  return x;
};

export const getChildrenIds = (id, mainTree) => {
  let ids = [id];
  let allNodes = [];
  const getId = (item) => item.id;
  const node = findNode(id, mainTree, "id", allNodes);
  const parent = allNodes[1];
  let siblings = [];
  let parentId;
  if (parent && parent.children) {
    parentId = parent.id;
    siblings = parent.children.map(getId);
  }
  const parents = allNodes.map(getId);
  if (node && node.children) {
    let holder = [];
    collectAllNodes(node, holder, ["para", "chapter"]);
    ids = [id, ...holder.map(getId)];
  }
  return { ids, siblings, parents, parentId };
};

export const detectIfLastRead = (luRead, luListen) => {
  if (luRead && luListen) {
    return dayjs(luRead).isAfter(luListen);
  } else if (luRead && !luListen) {
    return true;
  } else if (!luRead && luListen) {
    return false;
  }
  return true;
};

export const getSortDate = (luRead, luListen, showReadingHistory, showListenHistory) => {
  if (showReadingHistory && showListenHistory) {
    let readDate = luRead ? dayjs(luRead) : undefined;
    let listenDate = luListen ? dayjs(luListen) : undefined;
    if (readDate && listenDate) {
      return readDate.isAfter(listenDate) ? readDate : listenDate;
    } else if (readDate) {
      return readDate;
    } else if (listenDate) {
      return listenDate;
    }
  } else if (showReadingHistory && !showListenHistory && luRead) {
    return dayjs(luRead);
  } else if (!showReadingHistory && showListenHistory && luListen) {
    return dayjs(luListen);
  }
  return undefined;
};


export const makeTreeFromList = (data, options = {}) => {
  var ID_KEY = options.id || "id";
  var PARENT_KEY = options.parent || "topic";
  var CHILDREN_KEY = "children";

  var tree = [],
    childrenOf = {};
  var item, id, parentId;

  for (var i = 0, length = data.length; i < length; i++) {
    item = data[i];
    id = item[ID_KEY];
    parentId = item[PARENT_KEY] || 0;
    // every item may have children
    childrenOf[id] = childrenOf[id] || [];
    // init its children
    item[CHILDREN_KEY] = childrenOf[id];
    if (parentId != 0) {
      // init its parent's children object
      childrenOf[parentId] = childrenOf[parentId] || [];
      // push it into its parent's children object
      childrenOf[parentId].push(item);
    } else {
      tree.push(item);
    }
  }

  return tree;
};

/**
 * development
 * staging
 * production
 */

export const orderLanguages = (languages = [], addedLanguages = []) => {
  let resultItems = languages
    .filter((item) => addedLanguages.includes(item.id))
    .sort((prev, next) => {
      if (prev.id < next.id) {
        return -1;
      }
      if (prev.id > next.id) {
        return 1;
      }
      return 0;
    });

  resultItems = sortByArray(resultItems, addedLanguages, "id");
  return resultItems;
};

export const orderLanguagesDetect = (languages, addedLanguages) => {
  let resultItems = (languages || []).filter((item) => addedLanguages.includes(item.id));
  const defLang = addedLanguages[0];
  const defLangIndex = resultItems.findIndex((item) => item.id === defLang);
  if (defLangIndex !== -1) {
    arraymove(resultItems, defLangIndex, 0);
  }
  return resultItems;
};

/**
 * @description Returns window or document sizes (width and height). Specific
 * window object can be passed.
 * @returns {{width: number, height: number}}
 */
export const getWindowSizes = (windowObject = window) => {
  let width, height;

  // Fix for devices, that don't have innerWidth/Height properties. (e.g. old IOS)
  if (windowObject?.innerWidth === undefined) {
    width = windowObject.document.documentElement.clientWidth;
    height = windowObject.document.documentElement.clientHeight;
  } else {
    width = windowObject.innerWidth;
    height = windowObject.innerHeight;
  }

  return { width, height };
};

export const checkWordsNumber = (str) => !str || str.trim().split(" ").length > 1;

export const getBoundingClientRect = (target, returnOriginalRect = false) => {
  if (!target) {
    return { height: 0, width: 0, x: 0, y: 0, top: 0, left: 0, right: 0, bottom: 0 };
  }

  if (returnOriginalRect) {
    return target.getBoundingClientRect();
  }

  const { height, width, x, y, top, left, right, bottom } = target.getBoundingClientRect();

  return {
    height,
    width,
    left: left || x,
    top: top || y,
    x: left || x,
    y: top || y,
    right,
    bottom,
  };
};

/**
 * Check what button is clicked
 * @param {*} event
 */
export const buttonPressedCode = (event) =>
  event.buttons !== undefined ? event.buttons : event.nativeEvent.which;

export const findItemById = (list, id) => list.find((item) => item.id === id);

export const getSelectionAnchorClosest = (selection, selector) => {
  return selection?.anchorNode?.parentElement?.closest(selector) || null;
};

/**
 * @description Dispatches "name" mouse event on "target" in "touch" position.
 * @param {string} name
 * @param {Object} touch
 * @param {HTMLElement} target
 */
export const dispatchMouseEvent = (name, touch, target) => {
  const e = document.createEvent("MouseEvent");
  e.initMouseEvent(
    name,
    true,
    true,
    touch.view,
    1,
    touch.screenX,
    touch.screenY,
    touch.clientX,
    touch.clientY,
    false,
    false,
    false,
    false,
    0,
    null,
  );
  target.dispatchEvent(e);
};

export const osList = {
  MAC_OS: "MacOS",
  IOS: "iOS",
  ANDROID: "Android",
  WINDOWS: "Win",
  LINUX: "Linux",
};

export const detectOS = () => {
  if (typeof navigator !== "undefined") {
    const platform = navigator.platform.toLowerCase();

    if (getIsIos()) {
      return osList.IOS;
    }
    if (platform.includes("mac")) {
      return osList.MAC_OS;
    }
    if (/android/.test(navigator.userAgent.toLowerCase())) {
      return osList.ANDROID;
    }
    if (platform.includes("win")) {
      return osList.WINDOWS;
    }
    if (/linux/.test(platform)) {
      return osList.LINUX;
    }
  }

  return "unknown";
};

export const getExtendedClass = (index, isExpand, selected) => {
  let addClass = "";

  if (!isExpand) {
    addClass = "sideMenuItemCollapsed";
    if (selected !== -1 && index === selected) {
      addClass += " miSel";
    }
  } else if (selected !== -1 && index === selected) {
    addClass = "sideMenuSelected";
  } else if (selected !== -1 && Math.abs(selected - index) === 1 && isExpand) {
    addClass = "sideMenuBigger";
  } else {
    addClass = "sideMenuItemDefault";
  }

  return addClass;
};

/**
 * Returns new DOM element with "tag" name and copied "element" attributes.
 * @param {HTMLElement} element
 * @param {string} tag
 * @returns {HTMLElement}
 */
export const changeElementTag = (element, tag) => {
  const newElement = document.createElement(tag);
  const { attributes } = element;

  for (let i = 0, length = attributes.length; i < length; i++) {
    const { nodeName, nodeValue } = attributes.item(i);
    newElement.setAttribute(nodeName, nodeValue);
  }

  newElement.innerHTML = element.innerHTML;

  return newElement;
};

export const SCREEN_ORIENTATION = {
  PORTRAIT: "PORTRAIT",
  LANDSCAPE: "LANDSCAPE",
};

/**
 * Returns current screen orientation.
 * @param windowObject - for the test.
 * @param isTabletFlag - for the test.
 * @param isMobileFlag - for the test.
 * @returns {string}
 */
export const getScreenOrientation = (
  windowObject = window,
  isTabletFlag = isTablet,
  isMobileFlag = isMobile,
) => {
  let isLandscape = false;

  if (isMobileFlag || isTabletFlag) {
    if (windowObject.matchMedia("(min-aspect-ratio: 13/9)").matches) {
      isLandscape = true;
    }
  } else if (windowObject.innerWidth > windowObject.innerHeight) {
    isLandscape = true;
  }

  if (isLandscape) {
    return SCREEN_ORIENTATION.LANDSCAPE;
  }

  return SCREEN_ORIENTATION.PORTRAIT;
};

export const swapObjectsProperty = (obj1, obj2, keys) => {
  if (typeof keys === "string") {
    const temp = obj1[keys];
    obj1[keys] = obj2[keys];
    obj2[keys] = temp;
  } else if (keys instanceof Array) {
    for (let key of keys) {
      swapObjectsProperty(obj1, obj2, key);
    }
  }
};

export const findBible = (bibles, id) => bibles.length > 1 && bibles.find((b) => b.id === id);

export const getObjectKeysNotNullable = (obj) => Object.keys(obj).filter((key) => !!obj[key]);

export const removeDuplicatesFromPrimitives = (options) => [...new Set(options)];

export const isFunction = (func) => typeof func === "function";
